import { trackViewPromotionEvent } from "@ColonyHardware/Utils/tracking";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cartState: getCurrentCartState(state),
});

const enum fields {
    sectionHeading = "sectionHeading",
    image = "image",
    id = "id",
    creative_name = "creative_name",
    creative_slot = "creative_slot",
    title = "title",
    description = "description",
    items = "items",
    btnLabel = "btnLabel",
    buttonLink = "buttonLink",
}

type ItemModel = {
    fields: {
        image?: string;
        id?: string;
        creative_name?: string;
        creative_slot?: string;
        title?: string;
        description?: string;
        btnLabel?: string;
        buttonLink: LinkFieldValue;
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionHeading]: string;
        [fields.items]: ItemModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ColonyDifferenceStyles {
    container: InjectableCss;
    gridContainer: InjectableCss;
    titleLabel?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    btnWrapper?: InjectableCss;
    ctaButton?: ButtonPresentationProps;
    cardContainer?: GridItemProps;
    imageContainer?: GridItemProps;
    image?: ImgProps;
    contentContainer?: InjectableCss;
    sectionHeading?: TypographyPresentationProps;
}

export const colonyDifferenceStyles: ColonyDifferenceStyles = {
    container: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 24px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    gridContainer: {
        css: css`
            width: 100%;
            display: grid;
            gap: 2rem;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: repeat(1, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: repeat(2, 1fr);
                        `,
                    ],
                    "min",
                )}
        `,
    },
    titleLabel: {
        variant: "h3",
    },
    descriptionText: {
        variant: "p",
        css: css`
            margin-bottom: 40px;
        `,
    },
    ctaButton: {
        variant: "primary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        null,
                        css`
                            width: auto;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cardContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            border: 1px solid ${getColor("common.border")};
        `,
    },
    imageContainer: {
        css: css`
            width: 100%;
            > img {
                width: 100%;
                height: auto;
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            max-width: 100%;
        `,
    },
    contentContainer: {
        css: css`
            height: 100%;
            display: flex;
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 24px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    sectionHeading: {
        variant: "h3",
        fontFamily: "Inter-SemiBold",
        css: css`
            display: 100%;
            margin-bottom: 2rem;
        `,
    },
    btnWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: end;
        `,
    },
};

export const ColonyDifference = ({ fields, cartState }: Props) => {
    const styles = colonyDifferenceStyles;
    const { isInShell } = useShellContext();
    const history = useHistory();

    const handleBtnClick = (
        url: string | undefined,
        promotionId: string,
        promotionName: string,
        creativeName: string,
        creativeSlot: string,
    ) => {
        if (!url) {
            return;
        }
        let queryString = `&promotionId=${promotionId}&promotionName=${promotionName}`;

        queryString += creativeName && `&creativeName=${creativeName}`;
        queryString += creativeSlot && `&creativeSlot=${creativeSlot}`;

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            window.location.href = `${url}${queryString}`;
        } else {
            history.push(`${url}${queryString}`);
        }
    };

    const currentTitleAndId = () => {
        fields.items.map(item =>
            trackViewPromotionEvent(
                item.fields.id?.toLowerCase().replace(/\s+/g, ""),
                item.fields.title,
                item.fields.creative_name,
                item.fields.creative_slot,
            ),
        );
    };

    useEffect(() => {
        currentTitleAndId();
    }, []);

    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.sectionHeading}>{fields.sectionHeading}</Typography>
            <StyledWrapper {...styles.gridContainer}>
                {fields.items.map((item, i) => {
                    const { url } = useGetLink(item.fields.buttonLink);
                    return (
                        <StyledWrapper {...styles.cardContainer} key={`${i}-deal`}>
                            <StyledWrapper {...styles.imageContainer}>
                                <Img src={item.fields.image} alt="deal" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.contentContainer}>
                                <Typography {...styles.titleLabel}>{item.fields.title}</Typography>
                                <Typography {...styles.descriptionText}>
                                    {item.fields.description ? parse(item.fields.description, parserOptions) : ""}
                                </Typography>
                                <StyledWrapper {...styles.btnWrapper}>
                                    <Button
                                        variant="primary"
                                        {...styles.ctaButton}
                                        onClick={() =>
                                            handleBtnClick(
                                                url,
                                                item.fields.id ? item.fields.id.toLowerCase().replace(/\s+/g, "") : "",
                                                item.fields.title ? item.fields.title : "",
                                                item.fields.creative_name ? item.fields.creative_name : "",
                                                item.fields.creative_slot ? item.fields.creative_slot : "",
                                            )
                                        }
                                    >
                                        {item.fields.btnLabel}
                                    </Button>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ColonyDifference),
    definition: {
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.sectionHeading,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.items,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.image,

                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.id,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.creative_name,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.creative_slot,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.description,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.buttonLink,
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: fields.btnLabel,
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "Create an account",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
